import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import SchoolAssignmentsResult from "../../models/analytics/SchoolAssignmentsResult";
import DispatchProps from "../../utils/DispatchProps";
import ApiService from "../ApiService";
import { IRootState } from "../../store";
import PageTitle from "../PageTitle";
import AnalyticsDateControls from "../AnalyticsDateControls";
import H from "history";
import TenantAnalyticsList from "../TenantAnalyticsList";
import LoadingCard from "../LoadingCard";
import { Doughnut } from 'react-chartjs-2';
import { Icon } from 'office-ui-fabric-react';
import AcademicYearPicker from '../AcademicYearPicker';
import SchoolMenu from '../SchoolMenu';

interface TenantAssignmentAnalyticsPageProps {
    tenantId: string;
    history: H.History;
    startDate: string,
    endDate: string,
    academicYear: number;
    apiService: ApiService;
}

interface TenantAssignmentAnalyticsPageState {
    results?: SchoolAssignmentsResult[];
}

class TenantAssignmentAnalyticsPage extends Component<TenantAssignmentAnalyticsPageProps & DispatchProps, TenantAssignmentAnalyticsPageState> {
    static displayName = TenantAssignmentAnalyticsPage.name;

    constructor(props: TenantAssignmentAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = {
            results: undefined
        }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear) {
            await this.getResults();
        }
    }

    async componentDidUpdate(prevProps: Readonly<TenantAssignmentAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear) {
            await this.getResults();
        }
    }

    private async getResults(): Promise<void> {
        this.setState({
            results: undefined
        });
        const results = await this.props.apiService.getAssignmentAnalyticsForTenant(this.props.tenantId, this.props.startDate!, this.props.endDate!, this.props.academicYear);

        this.setState({
            results
        });
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private buildResults(): JSX.Element {
        if (this.state.results === undefined) {
            return <LoadingCard />;
        }
        else {

            var totalActiveAssignments = this.state.results.map(record => record.activeAssignments).reduce((sum, value) => sum + value, 0);
            var totalAssignmentsSet = this.state.results.map(record => record.totalAssignments).reduce((sum, value) => sum + value, 0);

            var schoolsWithActiveAssignment = this.state.results.filter((record, i, arr) => record.activeAssignments > 0).length;
            var schoolsWithoutActiveAssignment = this.state.results.filter((record, i, arr) => record.activeAssignments <= 0).length;

            var doughnutChartData = {
                labels: [
                    'Schools with active assignments',
                    'Schools without active assignments',
                ],
                datasets: [{
                    data: [schoolsWithActiveAssignment, schoolsWithoutActiveAssignment],
                    backgroundColor: [
                        '#99c140',
                        '#e7b416'
                    ],
                    hoverBackgroundColor: [
                        '#d0f261',
                        '#FFCE56',
                    ]
                }]
            };


            return <Fragment>
                <div className="flex-row flex--flow-wrap">
                    <div className="flex-column flex-item--flex-1">
                        <div className="flex-column flex-item--flex-1 p-left-1rem p-right-1rem p-top-1rem">
                            <div className="help-box-item help-box-item--wide" style={{maxWidth: 'none'}}>
                                <div className="help-box-item__icon">
                                    <Icon iconName="Chart" className="" />
                                </div>
                                <span className="help-box-item__title">Assignment analytics for all schools</span>
                                <span className="help-box-item__description">
                                    <span>Here you can see how different schools in your organisation are using assignments</span>
                                </span>
                            </div>
                        </div>

                        <div className="card flex-row flex--align-items-center flex--justify-content-space-around flex-item--flex-1">
                            <div className="number-stat">
                                <span className="number-stat__number number-stat__number--small">{totalActiveAssignments}</span>
                                <span className="number-stat__description">
                                    Total Active Assignments
                                </span>
                            </div>

                            <div className="number-stat">
                                <span className="number-stat__number number-stat__number--small">{totalAssignmentsSet}</span>
                                <span className="number-stat__description">
                                    Total Assignments Set
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="card card--padded flex-column flex-item--flex-1 flex--align-items-center flex--justify-content-space-around">
                        <div className="doughnut-holder">
                            <Doughnut data={doughnutChartData} height={1} width={1} />
                        </div>
                    </div>
                </div>

                <TenantAnalyticsList items={this.state.results} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} />
            </Fragment >
        }
    }

    render() {
        return (
            <Fragment>
                <PageTitle title={"Tenant Assignment Analytics"} />

                <SchoolMenu prefix="Assignment Analytics" />

                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end" }}>
                        <div>
                            <AcademicYearPicker history={this.props.history} onSelectedKeyChange={this.onAcademicYearChanged} />
                        </div>
                        <div>
                            <AnalyticsDateControls history={this.props.history} >
                                {AnalyticsDateControls.ASSIGNMENT_ANALYTICS_MESSAGE}
                            </AnalyticsDateControls>
                        </div>
                    </div>
    
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): TenantAssignmentAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        const tenantId = ownProps.match.params.tenantId;

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: tenantId,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(TenantAssignmentAnalyticsPage)